import { useEffect, useRef, useState } from 'react';
import { io, type Socket } from 'socket.io-client';
import { WS_NAMESPACE } from '@kick/types';
import { obtenerToken } from './auth-store';

const baseURL = import.meta.env.VITE_API_URL ?? 'http://localhost:3000';

export type EstadoConexion = 'conectando' | 'conectado' | 'desconectado';

/**
 * Conexión al canal del panel.
 *
 * Una sola por pestaña, compartida por todas las pantallas: abrir un socket
 * por componente multiplicaría las conexiones y los eventos duplicados.
 */
let socket: Socket | null = null;
const suscriptores = new Set<(estado: EstadoConexion) => void>();
let estadoActual: EstadoConexion = 'desconectado';

function avisar(estado: EstadoConexion): void {
  estadoActual = estado;
  suscriptores.forEach((s) => s(estado));
}

function conectar(): Socket {
  if (socket) return socket;

  socket = io(`${baseURL}${WS_NAMESPACE.MERCHANT}`, {
    // El token va en el handshake: el navegador no permite cabeceras en un WebSocket.
    auth: (cb) => cb({ token: obtenerToken() }),
    transports: ['websocket', 'polling'],
    reconnection: true,
    reconnectionDelay: 1000,
    reconnectionDelayMax: 10_000,
  });

  socket.on('connect', () => avisar('conectado'));
  socket.on('disconnect', () => avisar('desconectado'));
  socket.on('connect_error', () => avisar('desconectado'));
  socket.io.on('reconnect_attempt', () => avisar('conectando'));

  /**
   * El navegador sabe que se cayó la red antes que el socket.
   *
   * Sin esto habría que esperar al latido para enterarse, y el tablero
   * seguiría diciendo «en vivo» mientras ya no llega nada.
   */
  window.addEventListener('offline', () => avisar('desconectado'));
  window.addEventListener('online', () => sincronizar());

  return socket;
}

/**
 * Pone el estado mostrado de acuerdo con el socket real.
 *
 * Un corte breve puede no llegar a matar la conexión: el navegador avisa
 * `offline` y luego `online`, pero el socket nunca se enteró. Si en ese caso
 * se anunciara «reconectando», el indicador se quedaría así para siempre —no
 * habrá ningún evento `connect` que lo saque— aunque el canal esté perfecto.
 *
 * Volver a `conectado` también dispara la reconciliación en las pantallas, que
 * es lo que hace falta: mientras duró el corte pudieron perderse eventos.
 */
function sincronizar(): void {
  if (!socket) return;

  if (socket.connected) {
    avisar('conectado');
    return;
  }

  avisar('conectando');
  // `active` es false solo cuando el socket ya no piensa reintentar por su
  // cuenta; forzar `connect()` en medio de su ciclo lo deja colgado.
  if (!socket.active) socket.connect();
}

export function cerrarSocket(): void {
  socket?.disconnect();
  socket = null;
  avisar('desconectado');
}

/**
 * Suscribe un manejador a un evento mientras el componente esté montado.
 *
 * `manejador` se guarda en una referencia para no reconectar el listener en
 * cada render: pasar una función nueva cada vez haría que el efecto se
 * desmontara y volviera a montar constantemente, perdiendo eventos en medio.
 */
export function useEventoSocket<T>(evento: string, manejador: (datos: T) => void): void {
  const referencia = useRef(manejador);
  referencia.current = manejador;

  useEffect(() => {
    const s = conectar();
    const escuchar = (datos: T): void => referencia.current(datos);
    s.on(evento, escuchar);
    return () => {
      s.off(evento, escuchar);
    };
  }, [evento]);
}

/**
 * Estado de la conexión, para poder decírselo al usuario.
 *
 * Un tablero que dejó de recibir eventos sin avisar es peor que uno que
 * recarga cada 10 segundos: el comercio creería que no hay pedidos.
 */
export function useConexion(): EstadoConexion {
  const [estado, setEstado] = useState<EstadoConexion>(estadoActual);

  useEffect(() => {
    const s = conectar();
    setEstado(s.connected ? 'conectado' : 'conectando');
    suscriptores.add(setEstado);
    return () => {
      suscriptores.delete(setEstado);
    };
  }, []);

  return estado;
}
